import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Brain, Zap, Target, TrendingUp, Eye, MessageCircle, Lock } from 'lucide-react';
export function PSIProBenefitsSection() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  return <section className="relative py-12 sm:py-16 md:py-20 px-4 sm:px-6 bg-gradient-to-b from-black via-purple-950/20 to-black overflow-hidden" data-id="element-4200">
      {/* Mystical background effects - matching PSIGatheringSection */}
      <div className="absolute inset-0 opacity-10" style={{
      backgroundImage: `radial-gradient(circle at 20% 50%, rgba(168, 85, 247, 0.3) 0%, transparent 50%),
                           radial-gradient(circle at 80% 50%, rgba(59, 130, 246, 0.3) 0%, transparent 50%)`
    }} data-id="element-4201" />

      {/* Glowing orbs */}
      <div className="absolute top-1/4 left-1/3 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl animate-pulse-slow" data-id="element-4202" />
      <div className="absolute bottom-1/4 right-1/3 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl animate-pulse-slow" style={{
      animationDelay: '1s'
    }} data-id="element-4203" />

      <motion.div ref={ref} initial={{
      opacity: 0,
      y: 30
    }} animate={inView ? {
      opacity: 1,
      y: 0
    } : {}} transition={{
      duration: 0.8
    }} className="relative z-10 max-w-4xl mx-auto" data-id="element-4204">
        {/* Main headline */}
        <h2 className="text-2xl sm:text-4xl md:text-6xl font-serif text-center mb-8 sm:mb-12 leading-tight text-stone-100" style={{
        fontFamily: 'Cinzel, serif',
        textShadow: '0 0 30px rgba(168, 85, 247, 0.4)'
      }} data-id="element-4205">
          <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-purple-500 bg-clip-text text-transparent" style={{
          textShadow: '0 0 40px rgba(168, 85, 247, 0.6)'
        }} data-id="element-4206">
            PSI PRO
          </span>{' '}
          Is Where The Paradigm Shift Happens
        </h2>

        <p className="text-lg sm:text-xl text-stone-300 text-center mb-8 sm:mb-12 md:mb-16" data-id="element-4207">
          Here's what you're actually getting when you join:
        </p>

        {/* Benefits List */}
        <div className="space-y-16" data-id="element-4208">
          {/* Benefit 1: Paradigm Shift */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="relative" data-id="element-4209">
            <div className="absolute inset-0 bg-gradient-radial from-purple-600/10 via-transparent to-transparent blur-2xl" data-id="element-4210" />

            <div className="relative bg-gradient-to-br from-purple-950/40 to-black border-2 border-purple-500/50 p-4 sm:p-6 md:p-10 rounded-lg" data-id="element-4211">
              <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6" data-id="element-4212">
                <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-purple-500 rounded-full flex items-center justify-center" data-id="element-4213">
                  <Brain className="w-5 h-5 sm:w-6 sm:h-6 text-black" data-id="element-4214" />
                </div>
                <div className="flex-1" data-id="element-4215">
                  <div className="flex items-center gap-2 mb-3" data-id="element-4216">
                    <span className="text-green-400 text-xl sm:text-2xl" data-id="element-4217">
                      ✓
                    </span>
                    <h3 className="text-lg sm:text-2xl md:text-3xl font-bold text-purple-400" data-id="element-4218">
                      THE MOST IMPORTANT PARADIGM SHIFT YOU'LL EVER MAKE ABOUT
                      PEPTIDES
                    </h3>
                  </div>
                </div>
              </div>

              <div className="space-y-4 text-base sm:text-lg text-stone-300 pl-0 sm:pl-16" data-id="element-4219">
                <p className="text-stone-100 font-semibold" data-id="element-4220">
                  This is the shift that changes everything.
                </p>

                <div className="space-y-2 pl-6 border-l-2 border-purple-500/30" data-id="element-4221">
                  <p data-id="element-4222">
                    The shift that allows you to see what's ACTUALLY possible.
                  </p>
                  <p data-id="element-4223">
                    The shift that separates the masters from everyone else.
                  </p>
                </div>

                <p className="text-amber-400 font-bold" data-id="element-4224">
                  We've NEVER seen this covered by anyone else online. Ever.
                </p>

                <p data-id="element-4225">
                  And it's something Brenden and Ali will reveal during the live
                  strategic briefings.
                </p>

                <p className="italic text-stone-400" data-id="element-4226">
                  I can't tell you what it is on this page.
                </p>

                <p className="text-stone-400" data-id="element-4227">
                  (Because frankly, if I did, you'd think I was making it up.)
                </p>

                <div className="bg-purple-950/30 border-l-4 border-purple-500 p-6 my-6" data-id="element-4228">
                  <p className="text-xl text-stone-100 font-bold mb-3" data-id="element-4229">
                    But once you see it...
                  </p>
                  <p className="text-xl text-stone-100 font-bold mb-3" data-id="element-4230">
                    Once you UNDERSTAND it...
                  </p>
                  <p className="text-xl text-purple-400 font-bold" data-id="element-4231">
                    Everything else clicks into place.
                  </p>
                </div>

                <div className="space-y-2" data-id="element-4232">
                  <p data-id="element-4233">Your entire approach to biology changes.</p>
                  <p className="text-stone-100 font-semibold" data-id="element-4234">
                    And you'll wonder how you ever operated without knowing
                    this.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Benefit 2: The Impossible */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="relative" data-id="element-4235">
            <div className="absolute inset-0 bg-gradient-radial from-cyan-600/10 via-transparent to-transparent blur-2xl" data-id="element-4236" />

            <div className="relative bg-gradient-to-br from-cyan-950/40 to-black border-2 border-cyan-500/50 p-4 sm:p-6 md:p-10 rounded-lg" data-id="element-4237">
              <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6" data-id="element-4238">
                <div className="flex-shrink-0 w-12 h-12 bg-cyan-500 rounded-full flex items-center justify-center" data-id="element-4239">
                  <Zap className="w-6 h-6 text-black" data-id="element-4240" />
                </div>
                <div className="flex-1" data-id="element-4241">
                  <div className="flex items-center gap-2 mb-3" data-id="element-4242">
                    <span className="text-green-400 text-2xl" data-id="element-4243">✓</span>
                    <h3 className="text-2xl md:text-3xl font-bold text-cyan-400" data-id="element-4244">
                      HOW TO DO THE "IMPOSSIBLE" WITH HUMAN BIOLOGY
                    </h3>
                  </div>
                </div>
              </div>

              <div className="space-y-4 text-base sm:text-lg text-stone-300 pl-0 sm:pl-16" data-id="element-4245">
                <p data-id="element-4246">
                  This is where Brenden and Ali show you what's REALLY possible
                  when you understand peptides at the highest level.
                </p>

                <div className="space-y-2 pl-6 border-l-2 border-cyan-500/30" data-id="element-4247">
                  <p data-id="element-4248">Not just "optimization."</p>
                  <p data-id="element-4249">Not just "improvement."</p>
                </div>

                <p className="text-xl text-cyan-400 font-bold" data-id="element-4250">
                  But actual REGENERATION of tissues that mainstream medicine
                  considers "impossible to heal."
                </p>

                <div className="bg-cyan-950/30 p-6 rounded-lg space-y-3" data-id="element-4251">
                  <p className="text-stone-100" data-id="element-4252">
                    <span className="text-cyan-400 font-bold" data-id="element-4253">REVERSAL</span> of
                    conditions that doctors say are "permanent."
                  </p>
                  <p className="text-stone-100" data-id="element-4254">
                    <span className="text-cyan-400 font-bold" data-id="element-4255">RESTORATION</span>{' '}
                    of function that specialists say "can't be done."
                  </p>
                </div>

                <div className="space-y-2" data-id="element-4256">
                  <p className="text-stone-100 font-semibold" data-id="element-4257">
                    You'll see real case studies.
                  </p>
                  <p className="text-stone-100 font-semibold" data-id="element-4258">
                    Real transformations.
                  </p>
                  <p className="text-stone-100 font-semibold" data-id="element-4259">
                    Real "impossible" results.
                  </p>
                </div>

                <p className="text-xl text-cyan-400 font-bold" data-id="element-4260">
                  And you'll learn the EXACT frameworks they used to create
                  them.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Benefit 3: Precision Cell™ Pro */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="relative" data-id="element-4261">
            <div className="absolute inset-0 bg-gradient-radial from-emerald-600/10 via-transparent to-transparent blur-2xl" data-id="element-4262" />

            <div className="relative bg-gradient-to-br from-emerald-950/40 to-black border-2 border-emerald-500/50 p-4 sm:p-6 md:p-10 rounded-lg" data-id="element-4263">
              <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6" data-id="element-4264">
                <div className="flex-shrink-0 w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center" data-id="element-4265">
                  <Target className="w-6 h-6 text-black" data-id="element-4266" />
                </div>
                <div className="flex-1" data-id="element-4267">
                  <div className="flex items-center gap-2 mb-3" data-id="element-4268">
                    <span className="text-green-400 text-2xl" data-id="element-4269">✓</span>
                    <h3 className="text-2xl md:text-3xl font-bold text-emerald-400" data-id="element-4270">
                      THE PRECISION CELL™ PRO METHODOLOGY
                    </h3>
                  </div>
                </div>
              </div>

              <div className="space-y-4 text-base sm:text-lg text-stone-300 pl-0 sm:pl-16" data-id="element-4271">
                <p data-id="element-4272">
                  This is the same diagnostic and protocol design framework
                  Brenden and Ali use with private clients paying $15K−$120K.
                </p>

                <p className="text-emerald-400 font-bold" data-id="element-4273">
                  You're getting access to it as part of your PSI PRO
                  membership.
                </p>

                <p className="text-stone-100 font-semibold" data-id="element-4274">
                  Which means you'll learn:
                </p>

                <div className="space-y-3 pl-6 border-l-2 border-emerald-500/30" data-id="element-4275">
                  <p className="flex items-start gap-3" data-id="element-4276">
                    <span className="text-emerald-400 flex-shrink-0" data-id="element-4277">→</span>
                    <span data-id="element-4278">
                      How to think diagnostically instead of symptomatically
                    </span>
                  </p>
                  <p className="flex items-start gap-3" data-id="element-4279">
                    <span className="text-emerald-400 flex-shrink-0" data-id="element-4280">→</span>
                    <span data-id="element-4281">
                      How to identify root causes instead of just treating
                      surface issues
                    </span>
                  </p>
                  <p className="flex items-start gap-3" data-id="element-4282">
                    <span className="text-emerald-400 flex-shrink-0" data-id="element-4283">→</span>
                    <span data-id="element-4284">
                      How to design protocols based on molecular mechanisms
                    </span>
                  </p>
                  <p className="flex items-start gap-3" data-id="element-4285">
                    <span className="text-emerald-400 flex-shrink-0" data-id="element-4286">→</span>
                    <span data-id="element-4287">How to stack peptides for synergistic effects</span>
                  </p>
                  <p className="flex items-start gap-3" data-id="element-4288">
                    <span className="text-emerald-400 flex-shrink-0" data-id="element-4289">→</span>
                    <span data-id="element-4290">
                      How to optimize protocols based on real-time feedback
                    </span>
                  </p>
                  <p className="flex items-start gap-3" data-id="element-4291">
                    <span className="text-emerald-400 flex-shrink-0" data-id="element-4292">→</span>
                    <span data-id="element-4293">
                      How to create transformations for yourself and others
                    </span>
                  </p>
                </div>

                <p className="text-xl text-emerald-400 font-bold" data-id="element-4294">
                  This is what allows you to operate at a level most
                  practitioners will never reach.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Benefit 4: State of the Union */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="relative" data-id="element-4295">
            <div className="absolute inset-0 bg-gradient-radial from-blue-600/10 via-transparent to-transparent blur-2xl" data-id="element-4296" />

            <div className="relative bg-gradient-to-br from-blue-950/40 to-black border-2 border-blue-500/50 p-4 sm:p-6 md:p-10 rounded-lg" data-id="element-4297">
              <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6" data-id="element-4298">
                <div className="flex-shrink-0 w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center" data-id="element-4299">
                  <TrendingUp className="w-6 h-6 text-black" data-id="element-4300" />
                </div>
                <div className="flex-1" data-id="element-4301">
                  <div className="flex items-center gap-2 mb-3" data-id="element-4302">
                    <span className="text-green-400 text-2xl" data-id="element-4303">✓</span>
                    <h3 className="text-2xl md:text-3xl font-bold text-blue-400" data-id="element-4304">
                      THE STATE OF THE UNION: WHAT'S REALLY HAPPENING IN 2026
                    </h3>
                  </div>
                </div>
              </div>

              <div className="space-y-4 text-base sm:text-lg text-stone-300 pl-0 sm:pl-16" data-id="element-4305">
                <p data-id="element-4306">
                  Every month, Brenden and Ali will give you a STRATEGIC
                  BRIEFING about peptides, biohacking, healing or performance
                  enhancement.
                </p>

                <div className="space-y-3 pl-6 border-l-2 border-blue-500/30" data-id="element-4307">
                  <p data-id="element-4308">What's working in the real world.</p>
                  <p data-id="element-4309">What new research has emerged.</p>
                  <p data-id="element-4310">What misinformation is being spread.</p>
                  <p data-id="element-4311">Which vendors are legitimate and which are bunk.</p>
                  <p data-id="element-4312">What trends to pay attention to and what to ignore.</p>
                </div>

                <div className="bg-blue-950/30 p-6 rounded-lg space-y-3" data-id="element-4313">
                  <p className="text-xl text-blue-400 font-bold" data-id="element-4314">
                    This is your GROUNDED GUIDANCE.
                  </p>
                  <p className="text-stone-100" data-id="element-4315">
                    Your compass in a sea of noise.
                  </p>
                  <p className="text-stone-100" data-id="element-4316">
                    Your protection against the frauds, the liars, and the
                    grifters.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Benefit 5: Elite Secrets */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="relative" data-id="element-4317">
            <div className="absolute inset-0 bg-gradient-radial from-amber-600/10 via-transparent to-transparent blur-2xl" data-id="element-4318" />

            <div className="relative bg-gradient-to-br from-amber-950/40 to-black border-2 border-amber-500/50 p-4 sm:p-6 md:p-10 rounded-lg" data-id="element-4319">
              <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6" data-id="element-4320">
                <div className="flex-shrink-0 w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center" data-id="element-4321">
                  <Lock className="w-6 h-6 text-black" data-id="element-4322" />
                </div>
                <div className="flex-1" data-id="element-4323">
                  <div className="flex items-center gap-2 mb-3" data-id="element-4324">
                    <span className="text-green-400 text-2xl" data-id="element-4325">✓</span>
                    <h3 className="text-2xl md:text-3xl font-bold text-amber-400" data-id="element-4326">
                      ELITE, HIGH-LEVEL SECRETS YOU WON'T FIND ANYWHERE ELSE
                    </h3>
                  </div>
                </div>
              </div>

              <div className="space-y-4 text-base sm:text-lg text-stone-300 pl-0 sm:pl-16" data-id="element-4327">
                <p data-id="element-4328">
                  There are things Brenden and Ali will share during these
                  sessions that you will NEVER find outside of this membership.
                </p>

                <div className="space-y-3 pl-6 border-l-2 border-amber-500/30" data-id="element-4329">
                  <p data-id="element-4330">
                    Insights that would fundamentally change how you think about
                    biology.
                  </p>
                  <p data-id="element-4331">
                    Secrets that sound too good to be true... until you see the
                    science behind them.
                  </p>
                  <p data-id="element-4332">
                    Strategies that are so effective they border on unfair
                    advantages.
                  </p>
                </div>

                <p className="italic text-stone-400" data-id="element-4333">
                  I can't reveal what these are on this page.
                </p>

                <div className="bg-amber-950/30 p-6 rounded-lg" data-id="element-4334">
                  <p className="text-xl text-stone-100 font-bold mb-3" data-id="element-4335">
                    But I can tell you this:
                  </p>
                  <div className="space-y-2" data-id="element-4336">
                    <p data-id="element-4337">
                      If you're the kind of person who values being 10-20 years
                      ahead of the mainstream...
                    </p>
                    <p data-id="element-4338">
                      If you want access to knowledge that gives you a REAL
                      edge...
                    </p>
                    <p className="text-xl text-amber-400 font-bold" data-id="element-4339">
                      Then this alone is worth the price of admission.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Benefit 6: Monthly Q&A */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.5
        }} className="relative" data-id="element-4340">
            <div className="absolute inset-0 bg-gradient-radial from-rose-600/10 via-transparent to-transparent blur-2xl" data-id="element-4341" />

            <div className="relative bg-gradient-to-br from-rose-950/40 to-black border-2 border-rose-500/50 p-4 sm:p-6 md:p-10 rounded-lg" data-id="element-4342">
              <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6" data-id="element-4343">
                <div className="flex-shrink-0 w-12 h-12 bg-rose-500 rounded-full flex items-center justify-center" data-id="element-4344">
                  <MessageCircle className="w-6 h-6 text-black" data-id="element-4345" />
                </div>
                <div className="flex-1" data-id="element-4346">
                  <div className="flex items-center gap-2 mb-3" data-id="element-4347">
                    <span className="text-green-400 text-2xl" data-id="element-4348">✓</span>
                    <h3 className="text-2xl md:text-3xl font-bold text-rose-400" data-id="element-4349">
                      MONTHLY Q&A SESSIONS WHERE YOU CAN GET YOUR OWN QUESTIONS
                      ANSWERED
                    </h3>
                  </div>
                </div>
              </div>

              <div className="space-y-4 text-base sm:text-lg text-stone-300 pl-0 sm:pl-16" data-id="element-4350">
                <p className="text-xl text-rose-400 font-bold" data-id="element-4351">
                  This is where PSI PRO becomes PERSONAL.
                </p>

                <p data-id="element-4352">You're not just watching pre-recorded content.</p>

                <p className="text-stone-100 font-semibold" data-id="element-4353">
                  You're getting DIRECT ACCESS to Brenden and Ali.
                </p>

                <div className="space-y-3 pl-6 border-l-2 border-rose-500/30" data-id="element-4354">
                  <p data-id="element-4355">You can submit your questions in advance.</p>
                  <p data-id="element-4356">You can ask them live during the sessions.</p>
                  <p className="text-stone-100 font-semibold" data-id="element-4357">
                    You can get answers about YOUR specific situation.
                  </p>
                </div>

                <div className="bg-rose-950/30 p-6 rounded-lg space-y-3" data-id="element-4358">
                  <p className="text-stone-100 font-bold" data-id="element-4359">YOUR protocols.</p>
                  <p className="text-stone-100 font-bold" data-id="element-4360">YOUR results.</p>
                  <p className="text-stone-100 font-bold" data-id="element-4361">YOUR challenges.</p>
                </div>

                <p className="text-xl text-rose-400 font-bold" data-id="element-4362">
                  This is similar to the kind of access that private clients pay
                  $1,500/hour for.
                </p>

                <p className="text-stone-100 font-semibold" data-id="element-4363">
                  You're getting it as part of your membership.
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* CTA Button */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="mt-16 text-center" data-id="element-4364">
          <button onClick={() => {
          const pricingSection = document.querySelector('[data-pricing-section]');
          if (pricingSection) {
            pricingSection.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }} className="inline-block px-8 sm:px-12 py-4 sm:py-5 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-black font-bold text-lg sm:text-xl rounded-lg shadow-[0_0_30px_rgba(217,119,6,0.4)] hover:shadow-[0_0_50px_rgba(217,119,6,0.6)] hover:scale-105 transition-all duration-300 min-h-[44px]" data-id="element-4365">
            Join the Private Research Order Now
          </button>
        </motion.div>
      </motion.div>
    </section>;
}